import React, { useContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { getRequest, tabulatorGet } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { Dialog } from 'primereact/dialog';
import { MdSearch } from 'react-icons/md';
import { MdSearchOff } from 'react-icons/md';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {
  EditIcon,
  PrintIcon,
  ExportIcon,
  RefreshIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  EditRowIcon,
} from '../../svg/svgIcons';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { buildNavigationContextFromTable } from '../../../utils/maintenanceNavigationContext';
import { useTabulatorScrollArrows } from '../../../utils/useTabulatorScrollArrows';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createSaveUserMaskColumns,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  mergePagedStandardListFiltersAndSorters,
  appendMaintenanceCompanyListPagedFilter,
  applyCompanyListColumnLayout,
  restoreCompanyListUserMaskToTable,
  TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from '../../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../../utils/tabulatorIdColumn';
import {
  buildAuditTabulatorColumns,
  TABULATOR_AUDIT_PROFILES,
} from '../../../utils/tabulatorAuditColumns';
import {
  getCompanyListCanonicalFieldOrder,
  getCompanyListDefaultColumnVisibility,
  mergeCompanyListColumnVisibilityFromSaved,
  applyCompanyListDataColumnVisibility,
  COMPANY_LIST_VALIDITY_FILTER_OPTIONS,
  companyMaintenanceCertificatePreviewFilter,
} from '../../../utils/maintenanceCompanyListTabulator';
import { getPusherChannel } from '../../../utils/pusherChannel';
import { primeMultiSelectHeaderFilter } from '../../../utils/primeMultiSelectHeaderFilter';
import { LocaleContext } from '../../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../../constants/brandingDisplay';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../../utils/printChrome';
import {
  showDeactivatedInLists,
  showExcludedServiceCompaniesInLists,
} from '../../../utils/moduleSettings';
const MAINTENANCE_COMPANY_CERTIFICATE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'name': 'Common.ServiceCompanyName',
  'defined_id': 'Common.DefinedId',
  'tel_number': 'Common.Column.Phone',
  'contact_person': 'Common.ContactPerson',
  'description': 'Common.Description',
  'validity': 'Common.ServiceCompanyStatus',
  'rowSelection': 'Common.ServiceCompanyName',
  'address': 'BusinessPartnerList.Column.Address',
  'fax': 'BusinessPartnerList.Column.Fax',
  'email': 'Common.Email',
  'web_address': 'BusinessPartnerList.Column.WebAddress',
});



const MASK_PAGE = 'MaintenanceCompanyCertificateList';
const DEFAULT_COLUMN_VISIBILITY = getCompanyListDefaultColumnVisibility(MASK_PAGE);
const CANONICAL_FIELD_ORDER = getCompanyListCanonicalFieldOrder(MASK_PAGE);

export default function MaintenanceCompanyCertificateList({ preview = null }) {
  const { t, locale } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    getUserColor,
    getUserInitials,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [favoriteIcon] = useState(false);
  const [copyIcon, setCopyIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);

  const ref = useRef(null);
  const elementRef = useRef(null);
  const overlayRef = useRef(null);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 50;
  }, [state.module_settings]);
  const showExcludedCompanies = showExcludedServiceCompaniesInLists(state.module_settings);
  const showDeactivatedCerts = showDeactivatedInLists(state.module_settings, 'maintenance');

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const refreshFromStart = useCallback(
    () => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingData')),
    [getTable]
  );

  const buildNavContext = useCallback(
    () =>
      buildNavigationContextFromTable({
        table: getTable(),
        cleanSorters,
        extra: {
          type: 'maintenance',
          ...(searchValue?.trim() ? { search: searchValue.trim() } : {}),
        },
      }),
    [getTable, searchValue]
  );

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useTabulatorScrollArrows(getTable);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  const listType = 'maintenance';
  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };
    const onCreated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('company.created', id)) return;
      getRequest(`/api/companies/${id}?type=${listType}`)
        .then((res) => {
          const rowData = res?.data?.data ?? res?.data ?? res;
          if (!rowData?.id) return;
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        })
        .catch(() => {
          const table = getTable();
          const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
          if (row) row.delete();
        });
    };
    const onUpdated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('company.updated', id)) return;
      getRequest(`/api/companies/${id}?type=${listType}`)
        .then((res) => {
          const rowData = res?.data?.data ?? res?.data ?? res;
          if (!rowData?.id) return;
          const table = getTable();
          const row = table?.getRow?.(rowData.id) ?? table?.getRows?.().find((r) => r.getData().id === id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        })
        .catch(() => {
          const table = getTable();
          const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
          if (row) row.delete();
        });
    };
    const onDeleted = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('company.deleted', id)) return;
      const table = getTable();
      const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
      if (row) row.delete();
    };
    channel.bind('company.created', onCreated);
    channel.bind('company.updated', onUpdated);
    channel.bind('company.deleted', onDeleted);
    return () => {
      channel.unbind('company.created', onCreated);
      channel.unbind('company.updated', onUpdated);
      channel.unbind('company.deleted', onDeleted);
    };
  }, [getTable, listType]);

  const buildRequestParams = useCallback(
    (paramsFromTabulator = {}) => {
      const extra = { type: 'maintenance', ...extractSafePaginationParams(paramsFromTabulator) };
      if (extra.size == null) extra.size = pageSizePc;
      extra.show_excluded_companies = showExcludedCompanies ? '1' : '0';
      extra.show_deactivated_certs = showDeactivatedCerts ? '1' : '0';
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: preview == null ? state.user_mask_filters : null,
        maskPage: MASK_PAGE,
        appendFilter: appendMaintenanceCompanyListPagedFilter,
      });
      if (searchValue && String(searchValue).trim() !== '') extra.search = String(searchValue).trim();
      return extra;
    },
    [pageSizePc, getTable, searchValue, preview, state.user_mask_filters, showExcludedCompanies, showDeactivatedCerts]
  );

  const ajaxRequestFunc = useMemo(
    () => createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
    [buildRequestParams]
  );

  const remoteHeaderFilter = useCallback(() => true, []);

  function searchTable() {
    setSearchIconColor('active');
    if (searchValue === ' ' || (searchValue && String(searchValue).trim() === '')) {
      setSearchIconColor('');
      setSearchValue('');
      getTable()?.clearHeaderFilter?.();
      refreshFromStart();
    } else {
      captureHScroll();
      refreshFromStart();
    }
    setVisibleSearch(false);
  }

  const onSelectRow = (data) => {
    if (data.length === 0) {
      setEditIcon(true);
      setCopyIcon(true);
      setDeleteIcon(true);
    } else {
      setEditIcon(false);
      setCopyIcon(false);
      setDeleteIcon(false);
    }
  };

  function onEditRow(maintenance_company) {
    dispatch({
      type: 'create_new_tab', name: t('MaintenanceCompanyCertificateList.Text.Certifikaty_servisnej_firmy'), translationKey: 'MaintenanceCompanyCertificateList.Text.Certifikaty_servisnej_firmy', legacyLabel: t('MaintenanceCompanyCertificateList.Text.Certifikaty_servisnej_firmy'),
      page: 'MaintenanceCompanyCertificateAdd',
      props: { maintenance_company, type: 'edit', navigationContext: buildNavContext() },
      mask: 'mntn006',
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function MaintenanceCompanyValidityFormatter({ cell }) {
    const rowData = cell.getRow().getData();
    const v = rowData?.validity;
    if (v == 1 || v === '1') return <span>{t('Common.Active')}</span>;
    return <span>{t('QualityPartnerList.Status.Excluded')}</span>;
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        profile: TABULATOR_AUDIT_PROFILES.contractorLastTouched,
        includeId: false,
        dateFormat: dateFormatGlob,
        dateTimeFormat: dateTimeFormatGlob,
        getUserColor,
        getUserInitials,
        textHeaderFilter: remoteHeaderFilter,
        remotePassthrough: true,
      }),
    [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials, remoteHeaderFilter]
  );

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'rowSelection',
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        formatter: reactFormatter(<EditRowFormatter />),
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      buildTabulatorIdColumn({
        frozen: true,
        headerFilterFunc: remoteHeaderFilter,
      }),
      {
        title: t('Common.ServiceCompanyName'),
        field: 'name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ServiceCompanyName')),
        widthGrow: 1,
      },
      {
        title: t('Common.DefinedId'),
        field: 'defined_id',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.DefinedId')),
        widthGrow: 1,
      },
      {
        title: t('BusinessPartnerList.Column.Address'),
        field: 'address',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Adresa'),
        widthGrow: 1,
      },
      {
        title: t('Common.Column.Phone'),
        field: 'tel_number',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Column.Phone')),
        widthGrow: 1,
      },
      {
        title: t('BusinessPartnerList.Column.Fax'),
        field: 'fax',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Fax'),
        widthGrow: 1,
      },
      {
        title: t('Common.Email'),
        field: 'email',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('E-mail'),
        widthGrow: 1,
      },
      {
        title: t('BusinessPartnerList.Column.WebAddress'),
        field: 'web_address',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle('Web. adresa'),
        widthGrow: 1,
      },
      {
        title: t('Common.ContactPerson'),
        field: 'contact_person',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ContactPerson')),
        widthGrow: 1,
      },
      {
        title: t('Common.Description'),
        field: 'description',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Description')),
        widthGrow: 1,
      },
      {
        title: t('Common.ServiceCompanyStatus'),
        field: 'validity',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.ServiceCompanyStatus'),
          options: COMPANY_LIST_VALIDITY_FILTER_OPTIONS,
        },
        headerFilterFunc: remoteHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ServiceCompanyStatus'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<MaintenanceCompanyValidityFormatter />),
      },
      ...auditTailColumns,
    ],
    [auditTailColumns, remoteHeaderFilter]
  );

  function editMaintenanceCompanyCertificate() {
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (selected.length) onEditRow(selected[0]);
  }

  const itemRenderer = (item, options) => (
    <a
      className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]"
      onClick={options.onClick}
    >
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && (
        <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>
      )}
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});
  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MAINTENANCE_COMPANY_CERTIFICATE_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const hasApplicableStandardMask = useMemo(
    () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, (saved) => mergeCompanyListColumnVisibilityFromSaved(MASK_PAGE, saved)),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: (saved) => mergeCompanyListColumnVisibilityFromSaved(MASK_PAGE, saved),
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(
    createSaveUserMaskColumns({
      maskPage: MASK_PAGE,
      getTable,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      setMaskVisible,
      toast,
    }),
    [columnsVisible, getTable, setUserMaskFilter, toast]
  );

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: (saved) => mergeCompanyListColumnVisibilityFromSaved(MASK_PAGE, saved),
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        applyCompanyListColumnLayout(table, attributes, CANONICAL_FIELD_ORDER, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
        setColumnsVisible(colsForSave);
        applyCompanyListDataColumnVisibility(table, MASK_PAGE, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.redraw?.(true);
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: (table, vis) => applyCompanyListDataColumnVisibility(table, MASK_PAGE, vis),
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: (table, vis) => applyCompanyListDataColumnVisibility(table, MASK_PAGE, vis),
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: (table, vis) => applyCompanyListDataColumnVisibility(table, MASK_PAGE, vis),
    }),
    [getTable]
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: buildMaintenanceMaskAkcieSubItems({
        maskPage: MASK_PAGE,
        getTable,
        setUserMaskFilter,
        cleanSorters,
        captureHScroll,
        refreshFromStart,
        closeMenu,
        openMaskDialog,
        openChosenNotificationPreferences,
        hasApplicableStandardMask,
        confirmApplyStandardLayoutToUserMask,
        itemRenderer,
      }),
    },
  ];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button className="disabled:opacity-25" disabled={editIcon} type="button" onClick={() => editMaintenanceCompanyCertificate()}>
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor('');
                      setSearchValue('');
                      getTable()?.clearHeaderFilter?.();
                      refreshFromStart();
                    }
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" />
              ) : (
                <MdSearchOff fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={() => getTable()?.download?.('xlsx', 'data.xlsx', { sheetName: t('Maintenance.ServiceCompanyCertificates') })}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" onClick={() => scrollToFirst()}>
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" onClick={() => scrollUp()}>
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" onClick={() => scrollDown()}>
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" onClick={() => scrollToLast()}>
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button className="disabled:opacity-25" disabled={favoriteIcon} type="button" onClick={() => openFavoriteDialog(t('Training.TrainingCompanyCertificatesList'), MASK_PAGE)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView(MASK_PAGE, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={elementRef}>
                <ReactTabulator
                  data={undefined}
                  className="h-[calc(100vh_-_193px)]"
                  onRef={(r) => {
                    ref.current = r?.current;
                  }}
                  columns={columns}
                  events={{
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      isRestoringRef.current = true;
                      if (preview != null) {
                        table.setFilter(companyMaintenanceCertificatePreviewFilter, { time_period: preview });
                      }
                      const effective = preview == null ? getEffectiveMask(MASK_PAGE, state) : null;
                      if (effective?.type === 'user' && effective.userMask) {
                        restoreCompanyListUserMaskToTable(table, effective.userMask, {
                          canonicalFieldOrder: CANONICAL_FIELD_ORDER,
                          forceFrozenId: true,
                          setColumnsVisible,
                          applyColumnVisibilityFn: (tbl, jc, setVis) => {
                            const merged = mergeCompanyListColumnVisibilityFromSaved(MASK_PAGE, jc);
                            setVis(merged);
                            applyCompanyListDataColumnVisibility(tbl, MASK_PAGE, merged);
                          },
                        });
                      } else if (effective?.type === 'standard' && effective.standardMask) {
                        const m = effective.standardMask;
                        const attributes = safeParse(m.attributes, []);
                        const jsonColumns = safeParse(m.columns, {});
                        applyCompanyListColumnLayout(table, attributes, CANONICAL_FIELD_ORDER, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
                        if (jsonColumns && typeof jsonColumns === 'object') {
                          const merged = mergeCompanyListColumnVisibilityFromSaved(MASK_PAGE, jsonColumns);
                          setColumnsVisible(merged);
                          applyCompanyListDataColumnVisibility(table, MASK_PAGE, merged);
                        }
                      }
                      setTimeout(() => {
                        isRestoringRef.current = false;
                      }, 0);
                      tableBuiltRef.current = true;
                      refreshFromStart();
                    },
                    ...getTabulatorPersistLayoutEvents(MASK_PAGE, ref, setUserMaskFilter),
                    headerFilterChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    renderComplete: () => restoreHScroll(),
                  }}
                  options={{
                    layout: 'fitColumns',
                    renderHorizontal: 'virtual',
                    movableColumns: true,
                    headerSortTristate: true,
                    rowHeight: 42,
                    printAsHtml:true,printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('Print.Title.MaintenanceCompanyCertificateList'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                    printRowRange: 'active',
                    ajaxURL: '/api/companies/paged',
                    progressiveLoad: 'scroll',
                    progressiveLoadScrollMargin: 80,
                    paginationSize: pageSizePc,
                    pagination: false,
                    filterMode: 'remote',
                    sortMode: 'remote',
                    ajaxRequestFunc,
                    dataLoader: true,
                    dataLoaderLoading: t('Common.LoadingData'),
                    dataLoaderError: t('Common.LoadFailed'),
                    ajaxRequesting: () => {
                      if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                      return true;
                    },
                    ajaxResponse: (url, params, response) => {
                      getTable()?.clearAlert?.();
                      return response;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            className="flex flex-col flex-1 p-2 border border-gray-300 rounded"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t('Common.SearchPlaceholder')}
          />
          <button
            type="button"
            className="m-1 px-4 py-2 bg-zinc-900 text-white rounded hover:bg-zinc-700"
            onClick={searchTable}
          >{t('Common.Search')}</button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <Checkbox inputId={`mcertl-mask-${item.field}`} onChange={(e) => setMask(item.field, e.checked)} checked={columnsVisible[item.field] !== false} />
                <label htmlFor={`mcertl-mask-${item.field}`} className="ml-2 cursor-pointer">
                  {item.title}
                </label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
